'use client';

import { createContext, createElement, useContext, type ReactNode } from 'react';

import type { CityEdition } from '@loci/city-editions';

import type { CitySurfaceLabel } from './city-context';

/**
 * Client-side mirror of the server-resolved city context. The provider receives
 * the same value the server computed in `lib/city-context.ts`, so SSR'd markup
 * and hydrated markup agree — no hydration mismatch warning.
 *
 * Components that need the city in a client tree call `useCityContext()`
 * instead of reading headers (which aren't available on the client).
 */
const CityContext = createContext<CityEdition | null>(null);

interface CityContextProviderProps {
  value: CityEdition | null;
  children: ReactNode;
}

export function CityContextProvider({ value, children }: CityContextProviderProps) {
  return createElement(CityContext.Provider, { value }, children);
}

export function useCityContext(): CityEdition | null {
  return useContext(CityContext);
}

const CitySurfaceContext = createContext<CitySurfaceLabel>('fallback');

interface CitySurfaceProviderProps {
  value: CitySurfaceLabel;
  children: ReactNode;
}

export function CitySurfaceProvider({ value, children }: CitySurfaceProviderProps) {
  return createElement(CitySurfaceContext.Provider, { value }, children);
}

export function useCitySurface(): CitySurfaceLabel {
  return useContext(CitySurfaceContext);
}

/**
 * Request-host staging flag from `proxy.ts` `x-is-staging` (WEB-776).
 *
 * `undefined` when no provider is mounted (stories / unit tests) so URL
 * builders fall through to env / `window.location`. An explicit `true` or
 * `false` from the root layout wins — that is what keeps SSR chrome on
 * `staging-clt.loci.city` from emitting `app.loci.city` when the build-time
 * env is unset.
 */
const CityStagingContext = createContext<boolean | undefined>(undefined);

interface CityStagingProviderProps {
  value: boolean;
  children: ReactNode;
}

export function CityStagingProvider({ value, children }: CityStagingProviderProps) {
  return createElement(CityStagingContext.Provider, { value }, children);
}

export function useCityIsStaging(): boolean | undefined {
  return useContext(CityStagingContext);
}

/**
 * Request-time "this viewer carries a session" flag (WEB-1231 / R25), seeded
 * by the root layout from `accessToken` / `refreshToken` cookie presence.
 *
 * It decides which host a Discovery link targets, nothing else: a signed-in
 * person stays on `app.*`, an anonymous visitor stays on the city host. It is
 * cookie presence, not a validated identity — a stale cookie lands on the app
 * host, which bounces to `/login`, the same place the person would go to get
 * their session back. Never use it to show account data.
 *
 * SSR-seeded so server and hydrated markup emit the same href (no #418).
 * `false` without a provider (stories / unit tests): the anonymous links.
 */
const CityViewerSessionContext = createContext<boolean>(false);

interface CityViewerSessionProviderProps {
  value: boolean;
  children: ReactNode;
}

export function CityViewerSessionProvider({ value, children }: CityViewerSessionProviderProps) {
  return createElement(CityViewerSessionContext.Provider, { value }, children);
}

export function useCityViewerHasSession(): boolean {
  return useContext(CityViewerSessionContext);
}
